// client/src/components/EtsyShop.jsx
// Etsy Seller Management - Listings, Orders, Shop Dashboard

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppFooter from './AppFooter';
import { FaArrowLeft, FaHome, FaPlus, FaEdit, FaTrash, FaExternalLinkAlt, FaSync, FaBox, FaShoppingCart, FaStore, FaEye, FaHeart } from 'react-icons/fa';

const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin || 'http://localhost:5000';

function EtsyShop() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant) || Number(localStorage.getItem('tenantId')) || 1;

  // Tab state
  const [activeTab, setActiveTab] = useState('listings');

  // Connection state
  const [connected, setConnected] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [checkingConnection, setCheckingConnection] = useState(true);

  // Listings state
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsFilter, setListingsFilter] = useState('active');
  const [listingsCount, setListingsCount] = useState(0);

  // Orders state
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsCount, setReceiptsCount] = useState(0);

  // Create listing state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    price: '',
    quantity: 1,
    tags: '',
    whoMade: 'i_did',
    whenMade: 'made_to_order',
    isSupply: false,
    type: 'download',
  });

  // Edit listing state
  const [editingListing, setEditingListing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Shipping profiles
  const [shippingProfiles, setShippingProfiles] = useState([]);

  // Status messages
  const [statusMessage, setStatusMessage] = useState('');

  // Check connection on mount
  const checkConnection = useCallback(async () => {
    setCheckingConnection(true);
    try {
      const resp = await fetch(`${baseUrl}/api/integrations/etsy/peek?tenantId=${tenantId}`);
      const data = await resp.json();
      setConnected(data.connected);
      if (data.connected) {
        setShopInfo({ shopName: data.shopName, loginName: data.loginName });
      }
    } catch (err) {
      console.error('[EtsyShop] Connection check failed:', err);
    } finally {
      setCheckingConnection(false);
    }
  }, [tenantId]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Fetch listings
  const fetchListings = useCallback(async () => {
    if (!connected) return;
    setListingsLoading(true);
    try {
      const resp = await fetch(
        `${baseUrl}/api/integrations/etsy/listings?tenantId=${tenantId}&state=${listingsFilter}`
      );
      const data = await resp.json();
      if (data.success) {
        setListings(data.listings || []);
        setListingsCount(data.count || 0);
      } else {
        setStatusMessage(`Failed to load listings: ${data.error}`);
      }
    } catch (err) {
      setStatusMessage(`Error loading listings: ${err.message}`);
    } finally {
      setListingsLoading(false);
    }
  }, [connected, tenantId, listingsFilter]);

  // Fetch orders
  const fetchReceipts = useCallback(async () => {
    if (!connected) return;
    setReceiptsLoading(true);
    try {
      const resp = await fetch(
        `${baseUrl}/api/integrations/etsy/receipts?tenantId=${tenantId}`
      );
      const data = await resp.json();
      if (data.success) {
        setReceipts(data.receipts || []);
        setReceiptsCount(data.count || 0);
      } else {
        setStatusMessage(`Failed to load orders: ${data.error}`);
      }
    } catch (err) {
      setStatusMessage(`Error loading orders: ${err.message}`);
    } finally {
      setReceiptsLoading(false);
    }
  }, [connected, tenantId]);

  // Fetch shipping profiles
  const fetchShippingProfiles = useCallback(async () => {
    if (!connected) return;
    try {
      const resp = await fetch(
        `${baseUrl}/api/integrations/etsy/shipping-profiles?tenantId=${tenantId}`
      );
      const data = await resp.json();
      if (data.success) {
        setShippingProfiles(data.profiles || []);
      }
    } catch (err) {
      console.error('[EtsyShop] Failed to load shipping profiles:', err);
    }
  }, [connected, tenantId]);

  // Load data when tab changes
  useEffect(() => {
    if (!connected) return;
    if (activeTab === 'listings') fetchListings();
    if (activeTab === 'orders') fetchReceipts();
    if (activeTab === 'create') fetchShippingProfiles();
  }, [activeTab, connected, fetchListings, fetchReceipts, fetchShippingProfiles]);

  // Create listing
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newListing.title.trim()) {
      setStatusMessage('Title is required');
      return;
    }
    setCreating(true);
    setStatusMessage('');
    try {
      const tags = newListing.tags
        ? newListing.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const payload = {
        tenantId,
        title: newListing.title,
        text: newListing.description,
        price: newListing.price || 1,
        quantity: newListing.quantity || 1,
        tags,
        whoMade: newListing.whoMade,
        whenMade: newListing.whenMade,
        isSupply: newListing.isSupply,
      };

      if (newListing.type === 'physical' && newListing.shippingProfileId) {
        payload.shippingProfileId = newListing.shippingProfileId;
      }

      const resp = await fetch(`${baseUrl}/api/integrations/etsy/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      if (data.success) {
        setStatusMessage(`Listing created: ${data.listing?.title || data.listingId}`);
        setNewListing({
          title: '', description: '', price: '', quantity: 1, tags: '',
          whoMade: 'i_did', whenMade: 'made_to_order', isSupply: false, type: 'download',
        });
        setShowCreateForm(false);
        setActiveTab('listings');
        fetchListings();
      } else {
        setStatusMessage(`Failed: ${data.error}`);
      }
    } catch (err) {
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Update listing
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingListing) return;
    setSaving(true);
    setStatusMessage('');
    try {
      const resp = await fetch(
        `${baseUrl}/api/integrations/etsy/listings/${editingListing.listing_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            title: editingListing.title,
            description: editingListing.description,
            price: editingListing.price?.amount || editingListing.price,
            quantity: editingListing.quantity,
            tags: editingListing.tags,
          }),
        }
      );
      const data = await resp.json();
      if (data.success) {
        setStatusMessage('Listing updated');
        setEditingListing(null);
        fetchListings();
      } else {
        setStatusMessage(`Update failed: ${data.error}`);
      }
    } catch (err) {
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete listing
  const handleDelete = async (listingId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const resp = await fetch(
        `${baseUrl}/api/integrations/etsy/listings/${listingId}?tenantId=${tenantId}`,
        { method: 'DELETE' }
      );
      const data = await resp.json();
      if (data.success) {
        setStatusMessage('Listing deleted');
        fetchListings();
      } else {
        setStatusMessage(`Delete failed: ${data.error}`);
      }
    } catch (err) {
      setStatusMessage(`Error: ${err.message}`);
    }
  };

  // Connect handler
  const handleConnect = () => {
    const url = `${baseUrl}/api/integrations/etsy/login?tenantId=${encodeURIComponent(tenantId)}`;
    const w = window.open(url, 'etsy_oauth', 'width=700,height=800,menubar=no,toolbar=no,status=no');
    if (!w) {
      setStatusMessage('Popup was blocked. Please allow popups.');
      return;
    }
    w.focus?.();
    const onMsg = (ev) => {
      if (!ev?.data) return;
      if (ev.data.type === 'ETSY_OK') {
        window.removeEventListener('message', onMsg);
        setStatusMessage('Etsy connected!');
        checkConnection();
      } else if (ev.data.type === 'ETSY_ERR') {
        window.removeEventListener('message', onMsg);
        setStatusMessage(`Connection failed: ${ev.data.error}`);
      }
    };
    window.addEventListener('message', onMsg);
  };

  // Format price from Etsy's price object
  const formatPrice = (price) => {
    if (!price) return '--';
    if (typeof price === 'object') {
      return `${(price.amount / price.divisor).toFixed(2)} ${price.currency_code || ''}`.trim();
    }
    return `$${Number(price).toFixed(2)}`;
  };

  // Format timestamp
  const formatDate = (ts) => {
    if (!ts) return '--';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDashboard = () => navigate(`/${tenantId}/dashboard`);

  // ========== RENDER ==========

  if (checkingConnection) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Checking Etsy connection...</p>
      </div>
    );
  }

  if (!connected) {
    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '16px' }}>
          <FaStore style={{ marginRight: '8px' }} />
          Etsy Shop
        </h2>
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '24px', textAlign: 'center'
        }}>
          <p style={{ marginBottom: '16px', color: '#6b7280' }}>
            Connect your Etsy shop to manage listings and orders from On-Screen.
          </p>
          <button
            onClick={handleConnect}
            style={{
              padding: '12px 24px', backgroundColor: '#F56400', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Connect Etsy Account
          </button>
        </div>
        {statusMessage && <p style={{ marginTop: '12px', color: '#dc2626' }}>{statusMessage}</p>}
        <AppFooter buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: handleDashboard },
        ]} />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
          <FaStore style={{ marginRight: '8px', color: '#F56400' }} />
          {shopInfo?.shopName || 'Etsy Shop'}
        </h2>
        {shopInfo?.loginName && (
          <span style={{ color: '#6b7280', fontSize: '14px' }}>@{shopInfo.loginName}</span>
        )}
      </div>

      {/* Status message */}
      {statusMessage && (
        <div style={{
          padding: '8px 12px', marginBottom: '12px', borderRadius: '6px',
          backgroundColor: statusMessage.includes('Failed') || statusMessage.includes('Error')
            ? '#fef2f2' : '#f0fdf4',
          color: statusMessage.includes('Failed') || statusMessage.includes('Error')
            ? '#dc2626' : '#16a34a',
          fontSize: '14px',
        }}>
          {statusMessage}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e5e7eb', marginBottom: '16px' }}>
        {[
          { key: 'listings', label: 'Listings', icon: <FaBox /> },
          { key: 'orders', label: 'Orders', icon: <FaShoppingCart /> },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: activeTab === tab.key ? '600' : '400',
              color: activeTab === tab.key ? '#F56400' : '#6b7280',
              borderBottom: activeTab === tab.key ? '2px solid #F56400' : '2px solid transparent',
              marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ===== LISTINGS TAB ===== */}
      {activeTab === 'listings' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['active', 'draft', 'inactive', 'expired', 'sold_out'].map(state => (
                <button
                  key={state}
                  onClick={() => setListingsFilter(state)}
                  style={{
                    padding: '4px 10px', fontSize: '12px', borderRadius: '14px', border: '1px solid #d1d5db',
                    background: listingsFilter === state ? '#111827' : '#fff',
                    color: listingsFilter === state ? '#fff' : '#374151',
                    cursor: 'pointer',
                  }}
                >
                  {state.replace('_', ' ')}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={fetchListings}
                disabled={listingsLoading}
                style={{
                  padding: '6px 12px', fontSize: '13px', border: '1px solid #d1d5db',
                  borderRadius: '6px', background: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <FaSync className={listingsLoading ? 'spin' : ''} /> Refresh
              </button>
              <button
                onClick={() => { setShowCreateForm(true); setActiveTab('create'); }}
                style={{
                  padding: '6px 12px', fontSize: '13px', border: 'none', borderRadius: '6px',
                  background: '#F56400', color: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <FaPlus /> New Listing
              </button>
            </div>
          </div>

          {listingsLoading ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px' }}>Loading listings...</p>
          ) : listings.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px', background: '#f9fafb',
              borderRadius: '8px', border: '1px solid #e5e7eb',
            }}>
              <FaBox style={{ fontSize: '32px', color: '#d1d5db', marginBottom: '8px' }} />
              <p style={{ color: '#6b7280' }}>No {listingsFilter} listings found</p>
              <button
                onClick={() => { setShowCreateForm(true); setActiveTab('create'); }}
                style={{
                  marginTop: '12px', padding: '8px 16px', background: '#F56400', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                }}
              >
                Create Your First Listing
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                {listingsCount} {listingsFilter} listing{listingsCount !== 1 ? 's' : ''}
              </p>
              {listings.map(listing => (
                <div
                  key={listing.listing_id}
                  style={{
                    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px',
                    marginBottom: '10px', background: '#fff',
                  }}
                >
                  {/* Editing mode */}
                  {editingListing?.listing_id === listing.listing_id ? (
                    <form onSubmit={handleUpdate}>
                      <div style={{ marginBottom: '8px' }}>
                        <input
                          type="text"
                          value={editingListing.title}
                          onChange={e => setEditingListing({ ...editingListing, title: e.target.value })}
                          style={{
                            width: '100%', padding: '8px', border: '1px solid #d1d5db',
                            borderRadius: '4px', fontSize: '14px',
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <textarea
                          value={editingListing.description || ''}
                          onChange={e => setEditingListing({ ...editingListing, description: e.target.value })}
                          rows={3}
                          style={{
                            width: '100%', padding: '8px', border: '1px solid #d1d5db',
                            borderRadius: '4px', fontSize: '14px', resize: 'vertical',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={editingListing.editPrice || ''}
                          onChange={e => setEditingListing({ ...editingListing, editPrice: e.target.value })}
                          style={{
                            width: '100px', padding: '8px', border: '1px solid #d1d5db',
                            borderRadius: '4px', fontSize: '14px',
                          }}
                        />
                        <input
                          type="number"
                          min={0}
                          placeholder="Qty"
                          value={editingListing.quantity}
                          onChange={e => setEditingListing({ ...editingListing, quantity: Number(e.target.value) })}
                          style={{
                            width: '80px', padding: '8px', border: '1px solid #d1d5db',
                            borderRadius: '4px', fontSize: '14px',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="submit"
                          disabled={saving}
                          style={{
                            padding: '6px 14px', background: '#111827', color: '#fff',
                            border: 'none', borderRadius: '4px', fontSize: '13px', cursor: 'pointer',
                          }}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingListing(null)}
                          style={{
                            padding: '6px 14px', background: '#f3f4f6', color: '#374151',
                            border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    /* Display mode */
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600' }}>
                            {listing.title}
                          </h4>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6b7280' }}>
                            <span style={{ fontWeight: '600', color: '#111827' }}>
                              {formatPrice(listing.price)}
                            </span>
                            <span>Qty: {listing.quantity}</span>
                            {listing.views !== undefined && (
                              <span><FaEye style={{ marginRight: '2px', fontSize: '11px' }} />{listing.views}</span>
                            )}
                            {listing.num_favorers !== undefined && (
                              <span><FaHeart style={{ marginRight: '2px', fontSize: '11px', color: '#ef4444' }} />{listing.num_favorers}</span>
                            )}
                          </div>
                          {listing.tags?.length > 0 && (
                            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {listing.tags.slice(0, 5).map((tag, i) => (
                                <span key={i} style={{
                                  padding: '2px 6px', background: '#f3f4f6', borderRadius: '10px',
                                  fontSize: '11px', color: '#6b7280',
                                }}>
                                  {tag}
                                </span>
                              ))}
                              {listing.tags.length > 5 && (
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  +{listing.tags.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                          <button
                            onClick={() => setEditingListing({
                              ...listing,
                              editPrice: listing.price?.amount
                                ? (listing.price.amount / listing.price.divisor).toFixed(2)
                                : listing.price,
                            })}
                            title="Edit"
                            style={{
                              padding: '6px', background: 'none', border: '1px solid #d1d5db',
                              borderRadius: '4px', cursor: 'pointer', color: '#6b7280',
                            }}
                          >
                            <FaEdit />
                          </button>
                          <a
                            href={listing.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View on Etsy"
                            style={{
                              padding: '6px', border: '1px solid #d1d5db',
                              borderRadius: '4px', color: '#6b7280', display: 'flex', alignItems: 'center',
                            }}
                          >
                            <FaExternalLinkAlt />
                          </a>
                          <button
                            onClick={() => handleDelete(listing.listing_id, listing.title)}
                            title="Delete"
                            style={{
                              padding: '6px', background: 'none', border: '1px solid #fecaca',
                              borderRadius: '4px', cursor: 'pointer', color: '#dc2626',
                            }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                        Created {formatDate(listing.created_timestamp)}
                        {listing.state && listing.state !== listingsFilter && (
                          <span style={{
                            marginLeft: '8px', padding: '1px 6px', borderRadius: '10px',
                            background: '#fef3c7', color: '#92400e', fontSize: '10px',
                          }}>
                            {listing.state}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== CREATE LISTING TAB ===== */}
      {activeTab === 'create' && (
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Create New Listing</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Title *</label>
              <input
                type="text"
                value={newListing.title}
                onChange={e => setNewListing({ ...newListing, title: e.target.value })}
                placeholder="Handmade ceramic mug"
                required
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={newListing.description}
                onChange={e => setNewListing({ ...newListing, description: e.target.value })}
                placeholder="Describe your item in detail..."
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.20"
                  value={newListing.price}
                  onChange={e => setNewListing({ ...newListing, price: e.target.value })}
                  placeholder="19.99"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={newListing.quantity}
                  onChange={e => setNewListing({ ...newListing, quantity: Number(e.target.value) })}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Tags (comma-separated, max 13)</label>
              <input
                type="text"
                value={newListing.tags}
                onChange={e => setNewListing({ ...newListing, tags: e.target.value })}
                placeholder="handmade, ceramic, mug, gift"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Who Made</label>
                <select
                  value={newListing.whoMade}
                  onChange={e => setNewListing({ ...newListing, whoMade: e.target.value })}
                  style={inputStyle}
                >
                  <option value="i_did">I did</option>
                  <option value="someone_else">Someone else</option>
                  <option value="collective">A member of my shop</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>When Made</label>
                <select
                  value={newListing.whenMade}
                  onChange={e => setNewListing({ ...newListing, whenMade: e.target.value })}
                  style={inputStyle}
                >
                  <option value="made_to_order">Made to order</option>
                  <option value="2020_2026">2020-2026</option>
                  <option value="2010_2019">2010-2019</option>
                  <option value="2007_2009">2007-2009</option>
                  <option value="before_2007">Before 2007</option>
                  <option value="2000_2006">2000-2006</option>
                  <option value="1990s">1990s</option>
                  <option value="1980s">1980s</option>
                  <option value="1970s">1970s</option>
                  <option value="1960s">1960s</option>
                  <option value="1950s">1950s</option>
                  <option value="1940s">1940s</option>
                  <option value="1930s">1930s</option>
                  <option value="1920s">1920s</option>
                  <option value="1910s">1910s</option>
                  <option value="1900s">1900s</option>
                  <option value="1800s">1800s</option>
                  <option value="1700s">1700s</option>
                  <option value="before_1700">Before 1700</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Listing Type</label>
                <select
                  value={newListing.type}
                  onChange={e => setNewListing({ ...newListing, type: e.target.value })}
                  style={inputStyle}
                >
                  <option value="download">Digital Download</option>
                  <option value="physical">Physical Item</option>
                </select>
              </div>
              {newListing.type === 'physical' && (
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Shipping Profile</label>
                  <select
                    value={newListing.shippingProfileId || ''}
                    onChange={e => setNewListing({ ...newListing, shippingProfileId: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">Select profile...</option>
                    {shippingProfiles.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.title} ({p.processingDays})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={newListing.isSupply}
                  onChange={e => setNewListing({ ...newListing, isSupply: e.target.checked })}
                />
                This is a supply or tool
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                disabled={creating}
                style={{
                  padding: '10px 20px', background: '#F56400', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                  cursor: creating ? 'not-allowed' : 'pointer',
                }}
              >
                {creating ? 'Creating...' : 'Create Listing'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('listings')}
                style={{
                  padding: '10px 20px', background: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ===== ORDERS TAB ===== */}
      {activeTab === 'orders' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
              {receiptsCount} order{receiptsCount !== 1 ? 's' : ''}
            </p>
            <button
              onClick={fetchReceipts}
              disabled={receiptsLoading}
              style={{
                padding: '6px 12px', fontSize: '13px', border: '1px solid #d1d5db',
                borderRadius: '6px', background: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}
            >
              <FaSync className={receiptsLoading ? 'spin' : ''} /> Refresh
            </button>
          </div>

          {receiptsLoading ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px' }}>Loading orders...</p>
          ) : receipts.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px', background: '#f9fafb',
              borderRadius: '8px', border: '1px solid #e5e7eb',
            }}>
              <FaShoppingCart style={{ fontSize: '32px', color: '#d1d5db', marginBottom: '8px' }} />
              <p style={{ color: '#6b7280' }}>No orders yet</p>
            </div>
          ) : (
            <div>
              {receipts.map(receipt => (
                <div
                  key={receipt.receipt_id}
                  style={{
                    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px',
                    marginBottom: '10px', background: '#fff',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>
                        Order #{receipt.receipt_id}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                        {receipt.name || 'Buyer'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '15px', fontWeight: '600' }}>
                        {formatPrice(receipt.grandtotal)}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        {receipt.is_paid && (
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                            background: '#dcfce7', color: '#16a34a',
                          }}>
                            Paid
                          </span>
                        )}
                        {receipt.is_shipped ? (
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                            background: '#dbeafe', color: '#2563eb',
                          }}>
                            Shipped
                          </span>
                        ) : (
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                            background: '#fef3c7', color: '#92400e',
                          }}>
                            Not Shipped
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order items */}
                  {receipt.transactions?.length > 0 && (
                    <div style={{ marginTop: '10px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
                      {receipt.transactions.map(tx => (
                        <div key={tx.transaction_id} style={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: '13px', color: '#374151', padding: '2px 0',
                        }}>
                          <span>{tx.title} x{tx.quantity}</span>
                          <span>{formatPrice(tx.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                    {formatDate(receipt.create_timestamp)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AppFooter buttons={[
        { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
        { icon: <FaHome />, label: 'Home', onClick: handleDashboard },
      ]} />
    </div>
  );
}

// Shared styles
const labelStyle = {
  display: 'block', fontSize: '13px', fontWeight: '500',
  color: '#374151', marginBottom: '4px',
};

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
};

export default EtsyShop;
